import React from 'react';
import { X, FileText, User, Calendar, DollarSign, Clock, AlertCircle, ArrowRightLeft } from 'lucide-react';
import { Loan } from '../types';
interface LoanDetailPanelProps {
  loan: Loan | null;
  onClose: () => void;
  onReassign: (loanId: string) => void;
}
export function LoanDetailPanel({
  loan,
  onClose,
  onReassign
}: LoanDetailPanelProps) {
  if (!loan) return null;
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };
  const getDaysUntil = (dateStr: string) => {
    const diff = new Date(dateStr).getTime() - new Date().getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };
  const closingDays = getDaysUntil(loan.closingDate);
  const lockDays = getDaysUntil(loan.lockExpiration);
  const getUrgencyLabel = (days: number) => {
    if (days < 0) return {
      text: `${Math.abs(days)}d overdue`,
      color: 'text-red-600 bg-red-50'
    };
    if (days <= 3) return {
      text: `${days}d remaining`,
      color: 'text-red-600 bg-red-50'
    };
    if (days <= 7) return {
      text: `${days}d remaining`,
      color: 'text-amber-600 bg-amber-50'
    };
    return {
      text: `${days}d remaining`,
      color: 'text-gray-600 bg-gray-100'
    };
  };
  const closingUrgency = getUrgencyLabel(closingDays);
  const lockUrgency = getUrgencyLabel(lockDays);
  return <>
      <div className="fixed inset-0 bg-gray-900/30 backdrop-blur-sm z-40 transition-opacity" onClick={onClose} data-id="element-142"></div>

      <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col border-l border-gray-200" data-id="element-143">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white" data-id="element-144">
          <h2 className="text-base font-semibold text-gray-900" data-id="element-145">
            Loan Details
          </h2>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-146">
            <X className="w-5 h-5" data-id="element-147" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto" data-id="element-148">
          {/* Borrower Header */}
          <div className="px-6 pt-6 pb-5" data-id="element-149">
            <div className="flex items-start justify-between" data-id="element-150">
              <div data-id="element-151">
                <div className="flex items-center gap-2 mb-1" data-id="element-152">
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-153">
                    {loan.borrowerName}
                  </h3>
                  {loan.urgency === 'high' && <span className="bg-red-100 text-red-700 text-xs font-semibold px-2 py-0.5 rounded-full" data-id="element-154">
                      Urgent
                    </span>}
                  {loan.urgency === 'medium' && <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded-full" data-id="element-155">
                      Moderate
                    </span>}
                </div>
                <p className="text-sm text-gray-500" data-id="element-156">
                  {loan.loanName} · #{loan.loanId}
                </p>
              </div>
            </div>
          </div>

          {/* Action Button */}
          <div className="px-6 pb-6" data-id="element-157">
            <button onClick={() => onReassign(loan.id)} className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition-colors shadow-sm flex items-center justify-center gap-2" data-id="element-158">
              <ArrowRightLeft className="w-4 h-4" data-id="element-159" />
              Move to Retention
            </button>
          </div>

          {/* Key Metrics */}
          <div className="px-6 pb-6" data-id="element-160">
            <div className="grid grid-cols-2 gap-3" data-id="element-161">
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100" data-id="element-162">
                <div className="flex items-center gap-1.5 text-gray-500 mb-1.5" data-id="element-163">
                  <DollarSign className="w-3.5 h-3.5" data-id="element-164" />
                  <span className="text-xs font-medium uppercase tracking-wider" data-id="element-165">
                    Amount
                  </span>
                </div>
                <p className="text-lg font-bold text-gray-900 tabular-nums" data-id="element-166">
                  {formatCurrency(loan.loanAmount)}
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100" data-id="element-167">
                <div className="flex items-center gap-1.5 text-gray-500 mb-1.5" data-id="element-168">
                  <Calendar className="w-3.5 h-3.5" data-id="element-169" />
                  <span className="text-xs font-medium uppercase tracking-wider" data-id="element-170">
                    Closing
                  </span>
                </div>
                <p className="text-lg font-bold text-gray-900" data-id="element-171">
                  {formatDate(loan.closingDate)}
                </p>
                <span className={`inline-block text-xs font-medium mt-1 px-1.5 py-0.5 rounded ${closingUrgency.color}`} data-id="element-172">
                  {closingUrgency.text}
                </span>
              </div>
            </div>
          </div>

          {/* Lock Information */}
          <div className="px-6 pb-6" data-id="element-173">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3" data-id="element-174">
              Lock Information
            </h4>
            <div className="bg-gray-50 rounded-xl border border-gray-100 p-4" data-id="element-175">
              <div className="grid grid-cols-2 gap-4" data-id="element-176">
                <div data-id="element-177">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-178">Lock Date</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-179">
                    {formatDate(loan.lockDate)}
                  </p>
                </div>
                <div data-id="element-180">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-181">Expiration</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-182">
                    {formatDate(loan.lockExpiration)}
                  </p>
                  <span className={`inline-block text-xs font-medium mt-1 px-1.5 py-0.5 rounded ${lockUrgency.color}`} data-id="element-183">
                    {lockUrgency.text}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Team */}
          <div className="px-6 pb-6" data-id="element-184">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3" data-id="element-185">
              Team
            </h4>
            <div className="bg-gray-50 rounded-xl border border-gray-100 divide-y divide-gray-100" data-id="element-186">
              <div className="flex items-center justify-between px-4 py-3" data-id="element-187">
                <div className="flex items-center gap-2 text-gray-600" data-id="element-188">
                  <User className="w-4 h-4" data-id="element-189" />
                  <span className="text-sm" data-id="element-190">Processor</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-191">
                  {loan.processor}
                </span>
              </div>
              <div className="flex items-center justify-between px-4 py-3" data-id="element-192">
                <div className="flex items-center gap-2 text-gray-600" data-id="element-193">
                  <User className="w-4 h-4" data-id="element-194" />
                  <span className="text-sm" data-id="element-195">LOA</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-196">
                  {loan.loa}
                </span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="px-6 pb-8" data-id="element-197">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5" data-id="element-198">
              <FileText className="w-3.5 h-3.5" data-id="element-199" /> Escalation Notes
            </h4>
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4" data-id="element-200">
              <p className="text-sm text-amber-900 leading-relaxed" data-id="element-201">
                {loan.notes}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>;
}